import { isLocalDevelopmentHost } from "./hosts";

export const SCHOOL_WRAPPED_PREVIEW_CONTRACT_VERSION = "1";
export const SCHOOL_WRAPPED_DEFAULT_PREVIEW_URL =
  "https://school-wrapped.pages.dev/";
export const SCHOOL_WRAPPED_PRODUCTION_PREVIEW_ORIGIN =
  new URL(SCHOOL_WRAPPED_DEFAULT_PREVIEW_URL).origin;
export const LINK_ADMIN_PRODUCTION_PREVIEW_ORIGIN =
  "https://link-admin-3qw.pages.dev";

export const SCHOOL_WRAPPED_READY_MESSAGE_TYPE = "school-wrapped:ready";
export const SCHOOL_WRAPPED_PREVIEW_PAYLOAD_MESSAGE_TYPE =
  "school-wrapped:preview-payload";
export const SCHOOL_WRAPPED_PREVIEW_ERROR_MESSAGE_TYPE =
  "school-wrapped:preview-error";
export const SCHOOL_WRAPPED_PREVIEW_APPLIED_MESSAGE_TYPE =
  "school-wrapped:preview-applied";

export type SchoolWrappedPreviewContext = {
  contractVersion: typeof SCHOOL_WRAPPED_PREVIEW_CONTRACT_VERSION;
  projectId: string;
  schoolId: string;
  previewToken: string;
};

export type SchoolWrappedPreviewPayloadContext =
  SchoolWrappedPreviewContext & {
    payloadId: number;
  };

export type SchoolWrappedReadyMessage = SchoolWrappedPreviewContext & {
  type: typeof SCHOOL_WRAPPED_READY_MESSAGE_TYPE;
};

export type SchoolWrappedPreviewPayloadMessage =
  SchoolWrappedPreviewPayloadContext & {
    type: typeof SCHOOL_WRAPPED_PREVIEW_PAYLOAD_MESSAGE_TYPE;
    payload: unknown;
  };

export type SchoolWrappedPreviewErrorMessage =
  SchoolWrappedPreviewContext & {
    type: typeof SCHOOL_WRAPPED_PREVIEW_ERROR_MESSAGE_TYPE;
    message: string;
    payloadId?: number;
  };

export type SchoolWrappedPreviewAppliedMessage =
  SchoolWrappedPreviewPayloadContext & {
    type: typeof SCHOOL_WRAPPED_PREVIEW_APPLIED_MESSAGE_TYPE;
  };

export type SchoolWrappedPreviewMessage =
  | SchoolWrappedReadyMessage
  | SchoolWrappedPreviewPayloadMessage
  | SchoolWrappedPreviewErrorMessage
  | SchoolWrappedPreviewAppliedMessage;

export type SchoolWrappedPreviewConfig = {
  baseUrl: string;
  origin: string;
  error: string;
};

export type SchoolWrappedAdminPreviewParams = {
  adminPreview: boolean;
  parentOrigin: string;
  contractVersion: string;
  projectId: string;
  schoolId: string;
  previewToken: string;
};

const isLoopbackPreviewUrl = (url: URL) =>
  isLocalDevelopmentHost(url.hostname);

export const resolveSchoolWrappedPreviewConfig = (
  value?: string,
  options: { forbiddenOrigin?: string } = {},
): SchoolWrappedPreviewConfig => {
  const rawValue = value?.trim() || SCHOOL_WRAPPED_DEFAULT_PREVIEW_URL;
  const isExplicitValue = Boolean(value?.trim());
  try {
    const url = new URL(rawValue);
    if (
      url.protocol !== "https:" &&
      !(url.protocol === "http:" && isLoopbackPreviewUrl(url))
    ) {
      throw new Error("School wrapped preview URL must use https or local http.");
    }
    if (
      url.origin !== SCHOOL_WRAPPED_PRODUCTION_PREVIEW_ORIGIN &&
      !isLoopbackPreviewUrl(url)
    ) {
      throw new Error(
        "School wrapped preview URL must target school-wrapped production or local loopback.",
      );
    }
    if (options.forbiddenOrigin && url.origin === options.forbiddenOrigin) {
      throw new Error(
        "School wrapped preview URL must not share the parent origin.",
      );
    }
    return { baseUrl: url.toString(), origin: url.origin, error: "" };
  } catch (error) {
    if (!isExplicitValue) {
      const fallbackUrl = new URL(SCHOOL_WRAPPED_DEFAULT_PREVIEW_URL);
      return {
        baseUrl: fallbackUrl.toString(),
        origin: fallbackUrl.origin,
        error: "",
      };
    }
    return {
      baseUrl: "",
      origin: "",
      error:
        error instanceof Error
          ? error.message
          : "School wrapped preview URL is invalid.",
    };
  }
};

export const buildSchoolWrappedPreviewUrl = ({
  baseUrl,
  parentOrigin,
  context,
}: {
  baseUrl: string;
  parentOrigin: string;
  context: SchoolWrappedPreviewContext;
}) => {
  const url = new URL(baseUrl);
  url.searchParams.set("adminPreview", "1");
  const contextParams = new URLSearchParams();
  contextParams.set("contractVersion", context.contractVersion);
  contextParams.set("parentOrigin", parentOrigin);
  contextParams.set("projectId", context.projectId);
  contextParams.set("schoolId", context.schoolId);
  contextParams.set("previewToken", context.previewToken);
  url.hash = contextParams.toString();
  return url.toString();
};

export const parseSchoolWrappedAdminPreviewParams = (
  search: string,
  hash: string,
): SchoolWrappedAdminPreviewParams => {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
  return {
    adminPreview: searchParams.get("adminPreview") === "1",
    parentOrigin: hashParams.get("parentOrigin") ?? "",
    contractVersion: hashParams.get("contractVersion") ?? "",
    projectId: hashParams.get("projectId") ?? "",
    schoolId: hashParams.get("schoolId") ?? "",
    previewToken: hashParams.get("previewToken") ?? "",
  };
};

export const parseTrustedLinkAdminPreviewOrigins = (configured = "") =>
  new Set(
    configured
      .split(",")
      .map((origin) => origin.trim())
      .filter(Boolean)
      .map((origin) => {
        try {
          const url = new URL(origin);
          if (url.protocol !== "https:" || isLoopbackPreviewUrl(url)) return "";
          return url.origin;
        } catch {
          return "";
        }
      })
      .filter(Boolean),
  );

export const isTrustedLinkAdminPreviewOrigin = (
  origin: string,
  configured = "",
) => {
  if (!origin) return false;
  try {
    const url = new URL(origin);
    if (url.origin === LINK_ADMIN_PRODUCTION_PREVIEW_ORIGIN) return true;
    if (
      (url.protocol === "http:" || url.protocol === "https:") &&
      isLoopbackPreviewUrl(url)
    ) {
      return true;
    }
    return parseTrustedLinkAdminPreviewOrigins(configured).has(url.origin);
  } catch {
    return false;
  }
};

export const buildSchoolWrappedReadyMessage = (
  context: SchoolWrappedPreviewContext,
): SchoolWrappedReadyMessage => ({
  type: SCHOOL_WRAPPED_READY_MESSAGE_TYPE,
  ...context,
});

export const buildSchoolWrappedPreviewPayloadMessage = (
  context: SchoolWrappedPreviewPayloadContext,
  payload: unknown,
): SchoolWrappedPreviewPayloadMessage => ({
  type: SCHOOL_WRAPPED_PREVIEW_PAYLOAD_MESSAGE_TYPE,
  ...context,
  payload,
});

export const buildSchoolWrappedPreviewErrorMessage = (
  context: SchoolWrappedPreviewContext,
  message: string,
): SchoolWrappedPreviewErrorMessage => ({
  type: SCHOOL_WRAPPED_PREVIEW_ERROR_MESSAGE_TYPE,
  ...context,
  message,
});

export const buildSchoolWrappedPreviewAppliedMessage = (
  context: SchoolWrappedPreviewPayloadContext,
): SchoolWrappedPreviewAppliedMessage => ({
  type: SCHOOL_WRAPPED_PREVIEW_APPLIED_MESSAGE_TYPE,
  ...context,
});

export const hasSchoolWrappedPreviewContext = (
  value: unknown,
  context: SchoolWrappedPreviewContext,
) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value as Partial<SchoolWrappedPreviewContext>).contractVersion ===
        context.contractVersion &&
      (value as Partial<SchoolWrappedPreviewContext>).projectId ===
        context.projectId &&
      (value as Partial<SchoolWrappedPreviewContext>).schoolId ===
        context.schoolId &&
      (value as Partial<SchoolWrappedPreviewContext>).previewToken ===
        context.previewToken,
  );

export const hasSchoolWrappedPreviewPayloadContext = (
  value: unknown,
  context: SchoolWrappedPreviewPayloadContext,
) =>
  hasSchoolWrappedPreviewContext(value, context) &&
  Boolean(
    value &&
      typeof value === "object" &&
      (value as Partial<SchoolWrappedPreviewPayloadContext>).payloadId ===
        context.payloadId,
  );

export const isSchoolWrappedPreviewMessage = (
  value: unknown,
): value is SchoolWrappedPreviewMessage => {
  if (!value || typeof value !== "object" || !("type" in value)) return false;
  const type = (value as { type?: unknown }).type;
  if (type === SCHOOL_WRAPPED_READY_MESSAGE_TYPE) return true;
  if (type === SCHOOL_WRAPPED_PREVIEW_APPLIED_MESSAGE_TYPE) {
    return typeof (value as { payloadId?: unknown }).payloadId === "number";
  }
  if (type === SCHOOL_WRAPPED_PREVIEW_ERROR_MESSAGE_TYPE) {
    return (
      typeof (value as { message?: unknown }).message === "string" &&
      (!("payloadId" in value) ||
        typeof (value as { payloadId?: unknown }).payloadId === "number")
    );
  }
  if (type === SCHOOL_WRAPPED_PREVIEW_PAYLOAD_MESSAGE_TYPE) {
    return (
      "payload" in value &&
      typeof (value as { payloadId?: unknown }).payloadId === "number"
    );
  }
  return false;
};

export const isSchoolWrappedPreviewPayloadMessage = (
  value: unknown,
): value is SchoolWrappedPreviewPayloadMessage =>
  isSchoolWrappedPreviewMessage(value) &&
  value.type === SCHOOL_WRAPPED_PREVIEW_PAYLOAD_MESSAGE_TYPE;
